import {
  Button,
  DateRangePicker,
  DATE_INPUT_DEBOUNCE_MS,
  ErrorMessage,
  Modal,
  useCompleteSurveyResponseRequest,
  GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER,
} from '@employ-engage/common-components';
import { useFormik } from 'formik';
import {
  breakOutAnswersAndSubAnswers,
  buildReportDataColumns,
  ColumnDescriptor,
  ColumnType,
  coerceValue,
  ExportTypeEnum,
  FormResponse,
  FormVersion,
  getPresetRange,
  isCompleteRange,
  ReportFormVersion,
  ReportSummary,
  summarizeResponses,
  toRangeEnd,
  toRangeStart,
} from '@employ-engage/data';
import * as Yup from 'yup';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import xlsx from 'json-as-xlsx';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

const TYPE_BADGE_STYLES: Record<ColumnType, string> = {
  date:    'bg-blue-100 text-blue-700',
  text:    'bg-gray-100 text-gray-600',
  number:  'bg-purple-100 text-purple-700',
  boolean: 'bg-yellow-100 text-yellow-700',
  list:    'bg-green-100 text-green-700',
  unknown: 'bg-gray-50 text-gray-400',
};

const ColumnTypeSelector = ({
  column,
  override,
  onOverride,
}: {
  column: ColumnDescriptor;
  override?: ColumnType;
  onOverride: (type: ColumnType | undefined) => void;
}) => {
  if (!column.type || column.type === 'unknown') return null;
  const effectiveType = override ?? column.type;
  const options: ColumnType[] = [column.type, ...column.alternatives];
  if (options.length === 1) {
    return (
      <span className={`ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${TYPE_BADGE_STYLES[effectiveType]}`}>
        {effectiveType}
      </span>
    );
  }
  return (
    <span className="ml-2 inline-flex gap-1">
      {options.map((t) => (
        <button
          key={t}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onOverride(t === column.type && override === undefined ? undefined : t === column.type ? undefined : t);
          }}
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium transition-colors ${
            t === effectiveType
              ? TYPE_BADGE_STYLES[t]
              : 'bg-white border border-gray-300 text-gray-500 hover:bg-gray-50'
          }`}
        >
          {t}
        </button>
      ))}
    </span>
  );
};

const getSelectionKey = (
  templateId: string,
  startDate: string,
  endDate: string
) => `${templateId}|${startDate}|${endDate}`;

type TemplateOption = {
  templateId: string;
  name: string;
  /** Newest first. */
  versions: FormVersion[];
  isAnonymous: boolean;
};

type LoadedReport = {
  /** The template and dates this data was fetched for; see getSelectionKey. */
  key: string;
  templateId: string;
  sheetData: any[];
  summary: ReportSummary;
};

export interface ExportDataModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const ExportDataModal = ({ open, setOpen }: ExportDataModalProps) => {
  const { t } = useTranslation(['common']);
  const getSurveyResponses = useCompleteSurveyResponseRequest();
  const [loadedReport, setLoadedReport] = useState<LoadedReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [columnTypeOverrides, setColumnTypeOverrides] = useState<Record<string, ColumnType>>({});

  const requestIdRef = useRef(0);

  const { data } = useQuery(
    GET_ALL_FORM_VERSIONS_FOR_CURRENT_USER,
    {
      fetchPolicy: 'network-only',
    }
  );
  // Stable identity, so the memo and the fetch effect below don't re-run every render.
  const formVersions: FormVersion[] = useMemo(
    () => data?.formVersions ?? [],
    [data]
  );

  // Every edit to a template mints a new version, so reporting on a single
  // version cuts the history off at the last edit. Group them and report on
  // the template as a whole.
  const templateOptions: TemplateOption[] = useMemo(() => {
    const byTemplateId = new Map<string, FormVersion[]>();
    formVersions.forEach((formVersion) => {
      if (!formVersion.templateId) return;
      const versions = byTemplateId.get(formVersion.templateId) ?? [];
      versions.push(formVersion);
      byTemplateId.set(formVersion.templateId, versions);
    });

    return Array.from(byTemplateId.entries())
      .map(([templateId, versions]) => {
        const newestFirst = [...versions].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        return {
          templateId,
          name: newestFirst[0]?.name ?? templateId,
          versions: newestFirst,
          // If any version was anonymous, identity stays off for the whole export.
          isAnonymous: newestFirst.some((version) => version.isAnonymous),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [formVersions]);

  const downloadFile = (
    content: string,
    fileName: string,
    contentType: string
  ) => {
    const file = new Blob([content], { type: contentType });
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildSheetData = async (
    template: TemplateOption,
    startDate: string,
    endDate: string
  ) => {
    const responses = await getSurveyResponses({
      templateId: template.templateId,
      startDate: toRangeStart(startDate),
      endDate: toRangeEnd(endDate),
      isAnonymous: template.isAnonymous,
    });

    // Oldest version is version 1, matching how people count revisions.
    const versionLabelById = new Map(
      [...template.versions]
        .reverse()
        .map((version, index) => [
          version.id,
          `${version.name} (version ${index + 1})`,
        ])
    );

    const completedResponses = (responses || [])
      .filter((response: FormResponse) => response.dateCompleted)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((response: any) => ({
        ...response,
        templateVersionName:
          versionLabelById.get(response.templateVersionId) ?? '',
      }));

    return {
      sheetData: [
        {
          sheet: 'Raw Data',
          columns: [
            ...buildReportDataColumns(completedResponses, []),
            ...breakOutAnswersAndSubAnswers(
              completedResponses,
              template.versions as unknown as ReportFormVersion[]
            ),
          ],
          content: completedResponses,
        },
      ],
      summary: summarizeResponses(completedResponses),
    };
  };

  // Loaded data only counts when it was fetched for exactly what's selected
  // now. Otherwise a slow or failed reload would export the previous range.
  const getReportForSelection = (selection: {
    selectedTemplateId: string;
    startDate: string;
    endDate: string;
  }) =>
    loadedReport &&
    loadedReport.key ===
      getSelectionKey(
        selection.selectedTemplateId,
        selection.startDate,
        selection.endDate
      )
      ? loadedReport
      : null;

  // Takes the columns rather than reading them off `values`, so it writes out
  // what was submitted rather than whatever the last render happened to hold.
  const buildExportToExcelData = (
    report: LoadedReport,
    selectedColumns: ColumnDescriptor[]
  ) => {
    return new Promise<void>((resolve, reject) => {
      try {
        if (report.sheetData.length) {
          const sheet = report.sheetData[0];
          const selectedLabels = new Set(selectedColumns.map((c) => c.label));
          const filteredData = [
            {
              ...sheet,
              columns: sheet.columns
                .filter((col: ColumnDescriptor) => selectedLabels.has(col.label))
                .map((col: ColumnDescriptor) => {
                  const override = columnTypeOverrides[col.label];
                  if (!override || override === col.type) return col;
                  return { ...col, value: (row: any) => coerceValue(col.rawValue(row), override) };
                }),
            },
          ];
          xlsx(filteredData, {
            fileName: 'ReportBuilder',
          });
          resolve();
        } else {
          reject('Error building excel data');
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const {
    values,
    handleChange,
    handleSubmit,
    handleBlur,
    setFieldValue,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      selectedTemplateId: '',
      ...getPresetRange('thisMonth', new Date()),
      exportType: ExportTypeEnum.EXCEL,
      selectedColumns: [] as any[],
      targetDepartments: [] as any[],
    },
    validationSchema: Yup.object().shape({
      selectedTemplateId: Yup.string().required(t('Required')),
      startDate: Yup.string(),
      endDate: Yup.string(),
      exportType: Yup.string().required(t('Required')),
      selectedColumns: Yup.array().min(1, t('Required')),
    }),
    onSubmit: async (submittedValues) => {
      // The button is already disabled when the data doesn't match; this is
      // the backstop so a stale range can never be written out.
      const report = getReportForSelection(submittedValues);
      if (!report) return;

      if (submittedValues.exportType === ExportTypeEnum.JSON) {
        downloadFile(
          JSON.stringify(report.sheetData),
          'ReportBuilder.json',
          'application/json'
        );
        setOpen(false);
      } else if (submittedValues.exportType === ExportTypeEnum.EXCEL) {
        await buildExportToExcelData(report, submittedValues.selectedColumns);
        setOpen(false);
      }
    },
  });

  useEffect(() => {
    // Claim the id now, not when the debounce fires, so anything still in
    // flight for the previous selection is ignored the moment it changes.
    const requestId = ++requestIdRef.current;
    setLoadError(null);

    const template = templateOptions.find(
      (option) => option.templateId === values.selectedTemplateId
    );

    if (!template) {
      setLoadedReport(null);
      return;
    }
    // Typing a year steps through values like 0002-09-01. Keep the columns
    // on screen meanwhile — clearing them collapsed the modal and slid the
    // date fields out from under the cursor. Export stays disabled because
    // the loaded data no longer matches the selection.
    if (!isCompleteRange(values.startDate, values.endDate)) return;

    const key = getSelectionKey(
      template.templateId,
      values.startDate,
      values.endDate
    );

    const timer = setTimeout(() => {
      buildSheetData(template, values.startDate, values.endDate)
        .then(({ sheetData, summary }) => {
          if (requestId !== requestIdRef.current) return;
          setLoadedReport({
            key,
            templateId: template.templateId,
            sheetData,
            summary,
          });
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          console.error('Error building export data:', err);
          // Drop the previous range's data so it can't be exported by mistake.
          setLoadedReport(null);
          setLoadError(
            t("Couldn't load responses for these dates. Please try again.")
          );
        });
    }, DATE_INPUT_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    templateOptions,
    values.selectedTemplateId,
    values.startDate,
    values.endDate,
  ]);

  const currentReport = getReportForSelection(values);
  // Covers the debounce window too, so Export is never live between a date
  // change and its data arriving.
  const isLoading =
    !!values.selectedTemplateId &&
    isCompleteRange(values.startDate, values.endDate) &&
    !currentReport &&
    !loadError;
  // While a date change reloads, the same template's columns stay on screen so
  // the user's column picks don't flicker away; Export stays disabled meanwhile.
  const reportColumns: ColumnDescriptor[] =
    loadedReport?.templateId === values.selectedTemplateId
      ? loadedReport.sheetData[0]?.columns ?? []
      : [];

  const toggleSelectAllColumns = (checked: boolean) => {
    if (checked) {
      setFieldValue('selectedColumns', [...reportColumns]);
    } else {
      setFieldValue('selectedColumns', []);
    }
  };

  const handleToggleSelectedColumns = (datum: any) => {
    const isSelected = values.selectedColumns.some(
      (column) => column.label === datum.label
    );
    const next = isSelected
      ? values.selectedColumns.filter((column) => column.label !== datum.label)
      : [...values.selectedColumns, datum];
    setFieldValue('selectedColumns', next);
  };

  const shouldDisableSubmitButton = (values: any, errors: any) => {
    return (
      !values.selectedTemplateId ||
      !isCompleteRange(values.startDate, values.endDate) ||
      !currentReport ||
      currentReport.summary.count === 0 ||
      !values.exportType ||
      values.selectedColumns.length === 0 ||
      !!errors.selectedTemplateId ||
      !!errors.startDate ||
      !!errors.endDate ||
      !!errors.exportType
    );
  };

  return (
    // Pinned to the top with a fixed width, so the date fields stay put while
    // the column list below loads or changes. A stray click next to the
    // panel no longer throws away the setup; Cancel closes it.
    <Modal
      open={open}
      setOpen={setOpen}
      verticalAlign="top"
      disableCloseOnOutsideClick
      panelClassName="relative w-full sm:max-w-3xl"
    >
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <label
              htmlFor="selectedTemplateId"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('Template')}
            </label>
            <select
              id="selectedTemplateId"
              name="selectedTemplateId"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200"
              onChange={(e) => {
                handleChange(e);
                setFieldValue('selectedColumns', []);
                setColumnTypeOverrides({});
              }}
              onBlur={handleBlur}
              value={values.selectedTemplateId}
            >
              <option value="">{t('Select Template')}</option>
              {templateOptions.map((option) => {
                return (
                  <option key={option.templateId} value={option.templateId}>
                    {option.name}
                  </option>
                );
              })}
            </select>
            {errors.selectedTemplateId && touched.selectedTemplateId && (
              <ErrorMessage
                errors={errors.selectedTemplateId}
                touched={touched.selectedTemplateId}
              />
            )}
          </div>
          <div className="col-span-1">
            <label
              htmlFor="exportType"
              className="block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {t('Export Type')}
            </label>
            <select
              id="exportType"
              name="exportType"
              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md dark:bg-dark-primaryBackground dark:text-gray-200"
              onChange={handleChange}
              value={values.exportType}
            >
              <option value={ExportTypeEnum.EXCEL}>Excel</option>
              <option value={ExportTypeEnum.JSON}>JSON</option>
            </select>
            <ErrorMessage
              errors={errors.exportType}
              touched={touched.exportType}
            />
          </div>

          <DateRangePicker
            className="col-span-2 mb-6"
            legend={t('Date Range')}
            startDate={values.startDate}
            endDate={values.endDate}
            onChange={({
              startDate,
              endDate,
            }: {
              startDate: string;
              endDate: string;
            }) => {
              setFieldValue('startDate', startDate);
              setFieldValue('endDate', endDate);
            }}
          />
        </div>

        {!!reportColumns.length && !!values.selectedTemplateId && (
          <div className="p-5 shadow bg-white rounded-lg mb-5">
            <fieldset>
              <div className="flex justify-between">
                <legend className="text-base font-semibold leading-6 text-gray-900">
                  {t('Data Fields')}
                </legend>
                <div className="ml-3 flex h-6 items-center space-x-2">
                  <label
                    htmlFor="selectAll"
                    className="select-none font-medium text-gray-900"
                  >
                    {t('Select All')}
                  </label>
                  <input
                    id="selectAll"
                    type="checkbox"
                    checked={
                      values.selectedColumns.length === reportColumns.length
                    }
                    onChange={() =>
                      toggleSelectAllColumns(
                        values.selectedColumns.length !== reportColumns.length
                      )
                    }
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
              </div>
              <div className="mt-4 divide-y divide-gray-200 border-b border-t border-gray-200">
                {reportColumns.map((datum: any, index: number) => (
                  <div key={index} className="relative flex items-start py-4">
                    <div className="min-w-0 flex-1 text-sm leading-6">
                      <label
                        htmlFor={datum.label}
                        className="select-none font-medium text-gray-900 flex items-center flex-wrap gap-y-1"
                      >
                        {datum.label}
                        <ColumnTypeSelector
                          column={datum}
                          override={columnTypeOverrides[datum.label]}
                          onOverride={(t) =>
                            setColumnTypeOverrides((prev) =>
                              t === undefined
                                ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== datum.label))
                                : { ...prev, [datum.label]: t }
                            )
                          }
                        />
                      </label>
                    </div>
                    <div className="ml-3 flex h-6 items-center">
                      <input
                        id={datum.label}
                        name={datum.label}
                        type="checkbox"
                        checked={values.selectedColumns
                          .map((column: any) => column.label)
                          .includes(datum.label as never)}
                        onChange={() => handleToggleSelectedColumns(datum)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>
        )}

        <div className="mb-3 text-sm" aria-live="polite">
          {isLoading && (
            <p className="text-gray-600 dark:text-gray-300">
              <i className="fa-solid fa-spinner-third animate-spin mr-2" />
              {t('Loading responses…')}
            </p>
          )}
          {loadError && <p className="text-red-500">{loadError}</p>}
          {currentReport &&
            (currentReport.summary.count === 0 ||
            !currentReport.summary.firstCompleted ||
            !currentReport.summary.lastCompleted ? (
              <p className="text-gray-600 dark:text-gray-300">
                {t('No responses in this date range.')}
              </p>
            ) : (
              <p className="text-gray-700 dark:text-gray-200">
                {t('report-summary', {
                  count: currentReport.summary.count,
                  first: currentReport.summary.firstCompleted,
                  last: currentReport.summary.lastCompleted,
                })}
              </p>
            ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            className={classNames(
              'rounded-md bg-light-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-light-primary/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-light-primary',
              {
                'opacity-75 cursor-not-allowed': shouldDisableSubmitButton(
                  values,
                  errors
                ),
              }
            )}
            disabled={shouldDisableSubmitButton(values, errors)}
          >
            {t('export-to-type', { type: values.exportType })}
          </button>
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
